import * as React from 'react';
import {
   CustomCharacterCount,
   IsFocused,
   SessionStorage,
   OriginalState,
   SubmitError,
} from './extensions';
import { getExtensionsForPreset } from './presets';
import { useEditor } from '@tiptap/react';
import { setEditor } from './editor_cache';
import type { Editor } from '@tiptap/react';
import type { EditorPreset } from './presets';

interface IEditorContext {
   editor: Editor;
   getMinCharacters: () => number;
   getMaxCharacters: () => number;
   getNumCharacters: () => number;
   isFocused: () => boolean;
}

const editorContextDefaults: IEditorContext = {
   editor: null,
   getMinCharacters: () => 0,
   getMaxCharacters: () => 1024,
   getNumCharacters: () => 0,
   isFocused: () => false,
};

const EditorContext = React.createContext<IEditorContext>(editorContextDefaults);

export const useEditorContext = () => React.useContext(EditorContext);

export const withEditorContext = Component => {
   return function withEditorContextComponent(props: {
      editorid: string;
      text?: string;
      preset: EditorPreset;
      useCharacterCount?: boolean;
      maxCharacters?: number;
      minCharacters?: number;
   }) {
      const {
         editorid,
         text = '',
         preset,
         useCharacterCount = true,
         minCharacters = 12,
         maxCharacters = 1024,
      } = props;

      const extensions = [
         ...getExtensionsForPreset(preset),
         IsFocused,
         SessionStorage.configure({
            editorId: editorid,
            autosaveWait: 200,
         }),
         OriginalState.configure({
            savedContent: text,
            editorid,
         }),
         SubmitError.configure({}),
      ];

      if (useCharacterCount) {
         extensions.push(
            CustomCharacterCount.configure({
               minCharacters: minCharacters,
               limit: maxCharacters,
            })
         );
      }

      const editor = useEditor({
         extensions,
         content: parseEditorContent(text),
      });

      const editorContext: IEditorContext = {
         editor,
         getMinCharacters: () => editor?.storage.characterCount.minCharacters,
         getMaxCharacters: () => editor?.storage.characterCount.maxCharacters,
         getNumCharacters: () => editor?.storage.characterCount.characters(),
         isFocused: () => editor?.storage.isFocused.isFocused,
      };

      React.useEffect(() => {
         setEditor(editorid, editor);
      }, [editor]);

      return (
         <EditorContext.Provider value={editorContext}>
            <Component {...props} />
         </EditorContext.Provider>
      );
   };
};

const parseEditorContent = (text: string) => {
   if (!text) {
      return '';
   }
   try {
      return JSON.parse(text);
   } catch (e) {
      return text;
   }
};
